/**
 * OAuth callback route: /auth/callback
 *
 * Shopify redirects here after the customer completes authentication.
 * The `beforeLoad` exchanges the authorization code for tokens (server-side),
 * then the component stores the id_token in localStorage (client-side)
 * and navigates to the homepage.
 */
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { DEFAULT_LANGUAGE } from "@modules/shared/localization/const/language";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { LANGUAGE_KEY } from "../../../consts/keys";
import { getCookie } from "@/utils/cookies";
import { handleOAuthCallback } from "@/modules/auth/auth.api";

export const ID_TOKEN_STORAGE_KEY = "shopify-id-token";

export const Route = createFileRoute("/auth/callback")({
  validateSearch: (search: Record<string, unknown>) => ({
    code: search.code as string,
    state: search.state as string,
  }),
  beforeLoad: async ({ search }) => {
    const { code, state } = search;

    // Read the locale cookie so we can set i18next language correctly
    // (this route is outside $locale, so use the saved locale or default)
    const savedLocale = getCookie(LANGUAGE_KEY) || DEFAULT_LANGUAGE;

    if (!code || !state) {
      console.error("OAuth callback missing code or state params");
      throw redirect({
        to: "/$locale/login",
        params: { locale: savedLocale },
        replace: true,
      });
    }

    try {
      const result = await handleOAuthCallback({ data: { code, state } });
      // Pass the id_token and locale to the component via route context
      return { idToken: result.idToken, locale: savedLocale };
    } catch (error) {
      console.error("OAuth callback failed:", error);
      throw redirect({
        to: "/$locale/login",
        params: { locale: savedLocale },
        replace: true,
      });
    }
  },
  component: OAuthCallbackComponent,
});

/**
 * Stores the id_token in localStorage (for Shopify SSO logout)
 * then navigates to the homepage.
 */
function OAuthCallbackComponent() {
  const navigate = useNavigate();
  const { idToken, locale } = Route.useRouteContext();
  const { t } = useTranslation("Auth", {
    lng: locale,
  });

  useEffect(() => {
    // Store id_token in localStorage (not in a cookie — avoids header size issues
    // with Nitro dev proxy that rejects large cookie headers)
    if (idToken) {
      globalThis.localStorage.setItem(ID_TOKEN_STORAGE_KEY, idToken);
    }

    // Navigate to homepage with the user's current locale
    void navigate({
      to: "/$locale",
      params: { locale: locale || DEFAULT_LANGUAGE },
      replace: true,
    });
  }, [idToken, navigate, locale]);

  return (
    <div className="flex h-screen items-center justify-center">
      <p>{t("completingLogin")}</p>
    </div>
  );
}
